import "./fx-rack.css";

// FxRack — vertical, drag-to-reorder container for the FX column.
//
// Each child module gets a small `≡` grab handle injected into its
// header (any element with `.fx-module-header`). Pointer-drag on the
// handle picks the module up, the other modules slide to make space,
// and on release the rack reorders the DOM + fires `onReorder` with
// the new id sequence so the engine's `fx.slot_N` discrete params can
// be updated in lockstep.
//
// Heights are cached at drag-start and assumed stable during the drag.
// The dragged element's `translateY` follows the pointer exactly; the
// non-dragged elements get a single fixed offset (= dragged height)
// that toggles on/off as the dragged element's center crosses their
// midpoints. No CPU-heavy per-frame layout math.

export interface FxRackItem {
  id: string;
  element: HTMLElement;
  /** Optional human-readable name for screen-reader announcements. */
  label?: string;
}

export interface FxRackOptions {
  container: HTMLElement;
  items: FxRackItem[];
  /** Fired after each reorder (drag-drop or keyboard). Receives the new
   *  ids in top-to-bottom order. */
  onReorder: (newOrder: string[]) => void;
}

const HANDLE_SIZE = 24;

export class FxRack {
  private container: HTMLElement;
  private items: FxRackItem[];
  /** Current top-to-bottom order. Source of truth — DOM follows. */
  private order: string[];
  private onReorder: (newOrder: string[]) => void;
  private srAnnounce: HTMLElement;

  // ----- drag state -----
  private draggingId: string | null = null;
  private dragHandle: HTMLElement | null = null;
  /** Pointer Y at the moment drag started, in client coords. */
  private dragStartPointerY = 0;
  /** Heights (CSS px) of each item, snapshotted at drag start. */
  private heights = new Map<string, number>();
  /** Index in `order` the dragged item started at. */
  private dragStartIdx = -1;
  /** Index in `order` where the dragged item will land on drop. */
  private dragTargetIdx = -1;

  constructor(opts: FxRackOptions) {
    this.container = opts.container;
    this.container.classList.add("fx-rack");
    this.items = opts.items;
    this.order = opts.items.map((i) => i.id);
    this.onReorder = opts.onReorder;

    for (const item of this.items) {
      this.container.append(item.element);
      this.attachHandle(item);
    }

    // Hidden live region for screen-reader announcements after a reorder.
    this.srAnnounce = document.createElement("div");
    this.srAnnounce.className = "fx-rack-sr";
    this.srAnnounce.setAttribute("aria-live", "polite");
    this.srAnnounce.setAttribute("aria-atomic", "true");
    this.container.append(this.srAnnounce);
  }

  /** Current top-to-bottom order of FX ids. */
  getOrder(): string[] {
    return [...this.order];
  }

  /** Programmatically reorder the rack to match the given id sequence
   *  (e.g. for preset load). Unknown ids are dropped; missing ids stay
   *  in their previous relative order at the end. Fires onReorder so
   *  the engine's fx.slot_N values follow. */
  setOrder(newOrder: string[]): void {
    const known = new Set(this.order);
    const desired = newOrder.filter((id) => known.has(id));
    // Append any current ids not mentioned, preserving their existing
    // relative order — keeps unknown items from being lost.
    for (const id of this.order) {
      if (!desired.includes(id)) desired.push(id);
    }
    if (desired.length !== this.order.length) return;
    // No-op if already in this order.
    let same = true;
    for (let i = 0; i < desired.length; i++) {
      if (desired[i] !== this.order[i]) {
        same = false;
        break;
      }
    }
    if (same) return;
    this.order = desired;
    for (const id of this.order) {
      const el = this.itemFor(id)?.element;
      if (el) this.container.append(el);
    }
    this.container.append(this.srAnnounce);
    this.onReorder([...this.order]);
  }

  destroy(): void {
    this.container.classList.remove("fx-rack");
    this.srAnnounce.remove();
  }

  // ----- handle injection -----

  private attachHandle(item: FxRackItem): void {
    const header = item.element.querySelector<HTMLElement>(".fx-module-header");
    if (!header) return; // Module didn't expose a header — skip.

    const handle = document.createElement("button");
    handle.type = "button";
    handle.className = "fx-rack-handle";
    handle.setAttribute(
      "aria-label",
      `drag to reorder ${item.label ?? item.id}`,
    );
    handle.dataset["fxId"] = item.id;
    handle.append(drawHandleGlyph());
    header.prepend(handle);

    handle.addEventListener("pointerdown", (ev) => this.beginDrag(ev, item.id));
    handle.addEventListener("keydown", (ev) =>
      this.handleKeyboard(ev, item.id),
    );
  }

  // ----- drag start -----

  private beginDrag(ev: PointerEvent, id: string): void {
    if (this.draggingId !== null) return;
    ev.preventDefault();
    ev.stopPropagation();
    const handle = ev.currentTarget as HTMLElement;
    // Try to capture the pointer for completeness, but a failed capture
    // (synthetic events, browser quirks) shouldn't abort the drag —
    // document-scope listeners below handle move/up regardless.
    try {
      handle.setPointerCapture(ev.pointerId);
    } catch {
      // ignored
    }
    this.draggingId = id;
    this.dragHandle = handle;
    this.dragStartPointerY = ev.clientY;
    this.dragStartIdx = this.order.indexOf(id);
    this.dragTargetIdx = this.dragStartIdx;

    // Snapshot heights so per-frame math doesn't trigger layout reads.
    this.heights.clear();
    for (const it of this.items) {
      this.heights.set(it.id, it.element.getBoundingClientRect().height);
    }

    this.container.classList.add("is-dragging");
    this.itemFor(id)?.element.classList.add("is-fx-dragging");

    // Document-scope listeners keep firing even if the cursor leaves the
    // handle (which it will almost immediately on any meaningful drag).
    // Removed in `onEnd`.
    document.addEventListener("pointermove", this.onMove);
    document.addEventListener("pointerup", this.onEnd);
    document.addEventListener("pointercancel", this.onEnd);
  }

  // ----- drag move -----

  private onMove = (ev: PointerEvent): void => {
    if (this.draggingId === null) return;
    const deltaY = ev.clientY - this.dragStartPointerY;
    const draggedId = this.draggingId;

    // Translate the dragged element by the cursor delta. No transition
    // on `.is-fx-dragging` so this tracks the pointer exactly.
    const dragged = this.itemFor(draggedId);
    if (dragged) {
      dragged.element.style.transform = `translateY(${deltaY}px)`;
    }

    // Compute the dragged item's CURRENT visual center in container
    // coordinates. Use the original-order y positions since DOM order
    // hasn't changed yet — we'll commit on drop.
    const draggedH = this.heights.get(draggedId) ?? 0;
    const tops = this.cumulativeTops();
    const draggedOrigTop = tops[this.dragStartIdx] ?? 0;
    const draggedCenter = draggedOrigTop + draggedH / 2 + deltaY;

    // Count how many other items' midpoints the dragged item's center
    // has passed in each direction. That count is the new target index.
    let newIdx = this.dragStartIdx;
    for (let i = 0; i < this.order.length; i++) {
      if (i === this.dragStartIdx) continue;
      const id = this.order[i]!;
      const top = tops[i] ?? 0;
      const mid = top + (this.heights.get(id) ?? 0) / 2;
      if (i < this.dragStartIdx && draggedCenter < mid) {
        // Dragged is above this item's midpoint: target is no lower than i.
        newIdx = Math.min(newIdx, i);
      }
      if (i > this.dragStartIdx && draggedCenter > mid) {
        // Dragged is below this item's midpoint: target is at least i.
        newIdx = Math.max(newIdx, i);
      }
    }

    if (newIdx !== this.dragTargetIdx) {
      this.dragTargetIdx = newIdx;
      this.applyTargetSlotTranslations();
    }
  };

  /** Apply translateY to every non-dragged item to reflect the current
   *  drag-target slot — items between the dragged's original position
   *  and the target slot shift by ±draggedHeight to "make space". */
  private applyTargetSlotTranslations(): void {
    const draggedId = this.draggingId;
    if (draggedId === null) return;
    const draggedH = this.heights.get(draggedId) ?? 0;
    // Gap between items is set by container's gap; cached implicitly
    // because consecutive `top` positions account for it.
    const startIdx = this.dragStartIdx;
    const endIdx = this.dragTargetIdx;
    for (let i = 0; i < this.order.length; i++) {
      if (i === startIdx) continue;
      const id = this.order[i]!;
      const el = this.itemFor(id)?.element;
      if (!el) continue;
      let shift = 0;
      if (endIdx > startIdx && i > startIdx && i <= endIdx) {
        shift = -draggedH;
      } else if (endIdx < startIdx && i >= endIdx && i < startIdx) {
        shift = draggedH;
      }
      el.style.transform = shift !== 0 ? `translateY(${shift}px)` : "";
    }
  }

  // ----- drag end -----

  private onEnd = (ev: PointerEvent): void => {
    if (this.draggingId === null) return;
    const draggedId = this.draggingId;
    const newIdx = this.dragTargetIdx;
    const startIdx = this.dragStartIdx;
    const handle = this.dragHandle;
    const dragged = this.itemFor(draggedId);

    // Clear drag state first so any reorder side-effects see a clean
    // state (handlers do `if (this.draggingId !== null) return`).
    this.draggingId = null;
    this.dragHandle = null;
    if (handle) {
      try {
        handle.releasePointerCapture(ev.pointerId);
      } catch {
        // capture might not have been acquired — ignore
      }
    }
    document.removeEventListener("pointermove", this.onMove);
    document.removeEventListener("pointerup", this.onEnd);
    document.removeEventListener("pointercancel", this.onEnd);
    this.container.classList.remove("is-dragging");
    dragged?.element.classList.remove("is-fx-dragging");

    // Reset all transforms first, then reorder the DOM. The browser
    // will paint the new layout without a flash because the dragged
    // item's `transform` had been tracking the cursor exactly, and the
    // other items' transforms had been making space — the final DOM
    // position is where they all ended up visually.
    for (const it of this.items) {
      it.element.style.transform = "";
    }

    if (newIdx !== startIdx) {
      this.commitOrderChange(startIdx, newIdx);
      this.announceMove(draggedId, newIdx);
    }
  };

  // ----- commit reorder -----

  private commitOrderChange(fromIdx: number, toIdx: number): void {
    const [moved] = this.order.splice(fromIdx, 1);
    if (!moved) return;
    this.order.splice(toIdx, 0, moved);
    // Re-append elements in the new order; existing DOM nodes are
    // moved (not cloned) so all state (canvas contents, knob
    // listeners) stays intact.
    for (const id of this.order) {
      const el = this.itemFor(id)?.element;
      if (el) this.container.append(el);
    }
    // SR live region must remain the last child so it doesn't get
    // shuffled into the middle of the rack.
    this.container.append(this.srAnnounce);
    this.onReorder([...this.order]);
  }

  // ----- keyboard reorder -----

  private handleKeyboard(ev: KeyboardEvent, id: string): void {
    if (ev.key !== "ArrowUp" && ev.key !== "ArrowDown") return;
    const idx = this.order.indexOf(id);
    if (idx < 0) return;
    const newIdx = ev.key === "ArrowUp" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= this.order.length) return;
    ev.preventDefault();
    this.commitOrderChange(idx, newIdx);
    this.announceMove(id, newIdx);
    // Re-focus the handle so users can keep arrow-stepping.
    queueMicrotask(() => {
      const handle = this.itemFor(id)
        ?.element.querySelector<HTMLElement>(".fx-rack-handle");
      handle?.focus();
    });
  }

  // ----- helpers -----

  private itemFor(id: string): FxRackItem | undefined {
    return this.items.find((it) => it.id === id);
  }

  private cumulativeTops(): number[] {
    const tops: number[] = [];
    let y = 0;
    for (const id of this.order) {
      tops.push(y);
      y += this.heights.get(id) ?? 0;
    }
    return tops;
  }

  private announceMove(id: string, newIdx: number): void {
    const item = this.itemFor(id);
    const name = item?.label ?? id;
    this.srAnnounce.textContent = `${name} moved to position ${newIdx + 1}`;
  }
}

/** Render the `≡` drag glyph into a tiny canvas. Same canvas-glyph
 *  pattern the rest of the UI uses; respects DPR. */
function drawHandleGlyph(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  const dpr = window.devicePixelRatio || 1;
  canvas.width = HANDLE_SIZE * dpr;
  canvas.height = HANDLE_SIZE * dpr;
  canvas.style.width = `${HANDLE_SIZE}px`;
  canvas.style.height = `${HANDLE_SIZE}px`;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  // Paint in the dim grey used by other secondary glyphs (chevron in
  // Dropdown, anchor glyphs in MorphStrip, etc.). The button's
  // background tints around the glyph on hover — keeping the glyph
  // colour static avoids it flickering between states.
  ctx.strokeStyle = "rgba(124, 135, 148, 0.85)";
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  const inset = 6;
  const w = HANDLE_SIZE - 2 * inset;
  ctx.beginPath();
  // Three horizontal lines — classic "grab" affordance.
  for (let i = 0; i < 3; i++) {
    const y = HANDLE_SIZE / 2 - 5 + i * 5;
    ctx.moveTo(inset, y);
    ctx.lineTo(inset + w, y);
  }
  ctx.stroke();
  return canvas;
}
